/**
 * ネイティブ (iOS/Android) のカスタム URL スキーム受信。
 * Cognito Hosted UI (システムブラウザ) から `jp.kakumon.app://auth/callback|logout` への
 * リダイレクトを appUrlOpen イベントで受け取り、認可コード交換・ブラウザ閉じを行う。
 * 設計: .steering/20260719-capacitor-store-app/design.md §3.4
 */
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { NATIVE_REDIRECT_URI, NATIVE_LOGOUT_URI } from "./platform";

export interface AuthDeepLinkHandlers {
  /** callback URL を受信した（code が取れなかった場合は呼ばれない） */
  onAuthCallback: (code: string, state: string | null) => void;
  /** logout URL を受信した（Hosted UI のログアウト完了通知） */
  onAuthLogout: () => void;
}

/**
 * appUrlOpen の購読を開始する。戻り値の関数を呼ぶと購読解除する
 * （Web/PWA では @capacitor/app がノーオペのため何も起きない）。
 */
export function subscribeToAuthDeepLinks(
  handlers: AuthDeepLinkHandlers,
): () => void {
  let unsubscribed = false;
  let listenerHandle: { remove: () => void } | undefined;

  App.addListener("appUrlOpen", ({ url }) => {
    if (url.startsWith(NATIVE_REDIRECT_URI)) {
      Browser.close().catch(() => {});
      const parsed = new URL(url);
      const code = parsed.searchParams.get("code");
      const state = parsed.searchParams.get("state");
      if (code) {
        handlers.onAuthCallback(code, state);
      }
      return;
    }
    if (url.startsWith(NATIVE_LOGOUT_URI)) {
      Browser.close().catch(() => {});
      handlers.onAuthLogout();
    }
  }).then((handle) => {
    if (unsubscribed) {
      handle.remove();
    } else {
      listenerHandle = handle;
    }
  });

  return () => {
    unsubscribed = true;
    listenerHandle?.remove();
  };
}
